import { useRequest } from 'ahooks';
import { Avatar, List, Skeleton } from 'antd';
import CaregiverAvatar from 'avatar/CaregiverAvatar';
import CaregiverProfileModal from 'components/modals/CaregiverProfileModal';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import ApiRegisteredCaregiversManager from 'services/api/ApiRegisteredCaregiversManager';
import { colors } from 'design';

export type CaregiverAvatarListProps = {
  direction?: 'horizontal' | 'vertical';
  autoScroll?: boolean;
};

const useStyles = createUseStyles({
  mainContainer: (props: CaregiverAvatarListProps) => ({
    scrollbarWidth: 'none',
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    overflowX: 'auto',
    gap: 24,
    ...(props.direction === 'horizontal' ? { height: 'auto', overflowY: 'hidden', paddingBottom: 8 } : {}),
  }),
  qualificationContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  qualificationLevel: {
    fontSize: '0.85em',
    fontWeight: 500,
    color: colors.text.disabled,
    marginTop: 0,
    marginBottom: 0,
    textAlign: 'left',
  },
  listContainer: () => ({
    width: 'content-fit',
    display: 'flex',
    alignItems: 'flex-start',
    paddingRight: 16,
    gap: 16,
  }),
  horizontalListItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: '0 0 auto',
    cursor: 'pointer',
    minWidth: 'fit-content',
  },
  caregiverInfo: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  caregiverName: {
    fontSize: '1em',
    margin: 0,
    color: colors.text.primary,
    whiteSpace: 'nowrap',
    textAlign: 'left',
  },
  caregiverSpecialty: {
    fontSize: '0.75em',
    margin: 0,
    color: colors.text.tertiary,
    whiteSpace: 'nowrap',
    maxWidth: 80,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  skeletonContainer: {
    marginTop: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    width: '100%',
  },
});

const CaregiverAvatarList: React.FC<CaregiverAvatarListProps> = ({ direction, autoScroll }) => {
  // Hooks
  const { t } = useTranslation();
  const styles = useStyles({ direction });

  const containerRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const [selectedCaregiverId, setSelectedCaregiverId] = useState<string | null>(null);

  const { loading, data } = useRequest(() => ApiRegisteredCaregiversManager.getRegisteredCaregiversList(), {
    cacheKey: 'registered-caregivers.list',
  });

  const shuffledData = useMemo(() => {
    return data?.sort(() => Math.random() - 0.5);
  }, [data]);

  const initialLoading = data === undefined && loading;

  const caregiversByQualification = useMemo(() => {
    if (!shuffledData) return { medical: [], paramedical: [], alternative_medicine: [] };

    return shuffledData.reduce(
      (acc, caregiver) => {
        // Get the first group's qualification level (or default to paramedical)
        const qualificationLevel =
          caregiver.caregiver.caregiver_groups.length > 0
            ? caregiver.caregiver.caregiver_groups[0].qualification_level || 'paramedical'
            : 'paramedical';

        if (qualificationLevel === 'medical') {
          acc.medical.push(caregiver);
        } else if (qualificationLevel === 'paramedical') {
          acc.paramedical.push(caregiver);
        } else if (qualificationLevel === 'alternative_medicine') {
          acc.alternative_medicine.push(caregiver);
        }

        return acc;
      },
      { medical: [], paramedical: [], alternative_medicine: [] } as Record<string, typeof shuffledData>,
    );
  }, [shuffledData]);

  const clearAutoScroll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const scrollABit = useCallback(() => {
    if (containerRef.current && autoScroll) {
      containerRef.current.scrollTo({
        left: containerRef.current.scrollLeft + 1,
      });
    }
  }, [autoScroll]);

  const handleUserInteraction = useCallback(() => {
    clearAutoScroll();
  }, [clearAutoScroll]);

  useEffect(() => {
    if (autoScroll && containerRef.current) {
      // Clear any existing intervals/timeouts
      clearAutoScroll();

      // Start the scrolling interval
      intervalRef.current = setInterval(scrollABit, 40);

      // Clear the interval after 10 seconds
      timeoutRef.current = setTimeout(() => {
        clearAutoScroll();
      }, 30000);
    }

    // Cleanup on unmount or when autoScroll changes
    return () => {
      clearAutoScroll();
    };
  }, [autoScroll, scrollABit, clearAutoScroll]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !autoScroll) return;

    // Add event listeners for user interaction
    // Note: We don't listen to 'scroll' events because programmatic scrolling
    // would trigger them and stop the auto-scroll prematurely
    container.addEventListener('mousedown', handleUserInteraction);
    container.addEventListener('touchstart', handleUserInteraction);
    container.addEventListener('wheel', handleUserInteraction);

    return () => {
      container.removeEventListener('mousedown', handleUserInteraction);
      container.removeEventListener('touchstart', handleUserInteraction);
      container.removeEventListener('wheel', handleUserInteraction);
    };
  }, [autoScroll, handleUserInteraction]);

  return (
    <>
      {direction === 'horizontal' ? (
        <div className={styles.mainContainer} ref={containerRef}>
          {initialLoading && !data && (
            <div className={styles.skeletonContainer}>
              <Skeleton.Avatar active size={48} shape="circle" />
              <Skeleton.Avatar active size={48} shape="circle" />
              <Skeleton.Avatar active size={48} shape="circle" />
              <Skeleton.Avatar active size={48} shape="circle" />
            </div>
          )}
          {Object.entries(caregiversByQualification)
            .filter(([, caregivers]) => caregivers.length > 0)
            .map(([qualification, caregivers]) => (
              <div key={qualification} className={styles.qualificationContainer}>
                <p className={styles.qualificationLevel}>{t(`caregiverQualification.${qualification}`)}</p>
                <div className={styles.listContainer}>
                  {caregivers.map((caregiver, index) => {
                    const firstGroupName =
                      caregiver.caregiver.caregiver_groups.length > 0
                        ? caregiver.caregiver.caregiver_groups[0].display_name
                        : '';
                    const initials = caregiver.caregiver.first_name
                      .split(' ')
                      .map(n => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2);

                    return (
                      <div
                        key={`avatar-${index}`}
                        className={styles.horizontalListItem}
                        onClick={() => setSelectedCaregiverId(caregiver.user.id)}
                      >
                        <Avatar src={caregiver.user.photo_url} size={40} style={{ flex: '0 0 auto' }}>
                          {initials}
                        </Avatar>
                        <div className={styles.caregiverInfo}>
                          <p className={styles.caregiverName}>{caregiver.caregiver.first_name}</p>
                          <p className={styles.caregiverSpecialty}>{firstGroupName}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
        </div>
      ) : (
        // Vertical list with qualification grouping
        <div style={{ width: '100%' }}>
          {Object.entries(caregiversByQualification)
            .filter(([, caregivers]) => caregivers.length > 0)
            .map(([qualification, caregivers]) => (
              <div key={qualification} style={{ marginBottom: 24 }}>
                <h3
                  style={{
                    marginBottom: 16,
                    color: colors.text.disabled,
                    fontWeight: 'bold',
                    textAlign: 'center',
                  }}
                >
                  {t(`caregiverQualification.${qualification}`)}
                </h3>
                <List
                  itemLayout="horizontal"
                  grid={{ gutter: 4, column: Math.min(4, caregivers.length) }}
                  dataSource={caregivers}
                  loading={initialLoading}
                  renderItem={(item, index) => {
                    const firstGroupName =
                      item.caregiver.caregiver_groups.length > 0 ? item.caregiver.caregiver_groups[0].display_name : '';

                    return (
                      <List.Item onClick={() => setSelectedCaregiverId(item.user.id)}>
                        <CaregiverAvatar
                          key={`avatar-${index}`}
                          avatar={item.user.photo_url}
                          name={item.caregiver.first_name}
                          caption={firstGroupName}
                        />
                      </List.Item>
                    );
                  }}
                />
              </div>
            ))}
        </div>
      )}
      {selectedCaregiverId && (
        <CaregiverProfileModal caregiverId={selectedCaregiverId} open onClose={() => setSelectedCaregiverId(null)} />
      )}
    </>
  );
};
export default CaregiverAvatarList;
